import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';
import type { Vendor } from '@/services/matching.service';
import {
    type VendorRoleType,
    VENDOR_ROLE,
    VENDOR_ROLE_CONFIG,
    type VendorRoleConfig,
} from '@/constants/vendorRole';

/**
 * shopStore: 현재 접속한 쇼핑몰의 정보를 저장하는 스토어
 * - URL의 vendorPath로 조회한 쇼핑몰 주인(도매업자) 정보
 * - role로 소매/도매 쇼핑몰 구분
 *
 * 주의: 이 스토어는 '로그인한 사용자'가 아닌 '쇼핑몰 주인'의 정보입니다.
 */

export interface ShopStoreState {
    /** 현재 접속한 쇼핑몰의 주인(도매업자) 정보 */
    shop: Vendor | null;
}

export interface ShopStoreAction {
    /** 스토어 초기화 */
    reset: () => void;
    /** 쇼핑몰 정보 설정 */
    setShop: (shop: Vendor | ((prev: Vendor | null) => Vendor | null)) => void;

    // 역할 관련 getter
    /** 쇼핑몰의 역할(소매/도매) 조회 */
    getShopRole: () => VendorRoleType | null;
    /** 도매 쇼핑몰 여부 */
    isSupplierShop: () => boolean;
    /** 소매 쇼핑몰 여부 */
    isResellerShop: () => boolean;
    /** 역할 설정 조회 */
    getRoleConfig: () => VendorRoleConfig | null;
}

const defaultState: ShopStoreState = {
    shop: null,
};

export const useShopStore = create<ShopStoreState & ShopStoreAction>()(
    persist(
        (set, get) => ({
            ...defaultState,
            reset: () => {
                set(() => ({ ...defaultState }));
            },
            setShop: (shop) => {
                set((state) => ({
                    shop: typeof shop === 'function' ? shop(state.shop) : shop,
                }));
            },

            // 역할 관련 메서드
            getShopRole: () => get().shop?.role ?? null,
            isSupplierShop: () => get().shop?.role === VENDOR_ROLE.SUPPLIER,
            isResellerShop: () => get().shop?.role === VENDOR_ROLE.RESELLER,
            getRoleConfig: () => {
                const role = get().shop?.role;
                return role ? VENDOR_ROLE_CONFIG[role] : null;
            },
        }),
        {
            name: 'shop-storage',
            storage: createJSONStorage(() => sessionStorage),
        },
    ),
);

// 비-React 환경에서 사용할 수 있는 스토어 인스턴스
export const shopStore = useShopStore;
